import React, {useMemo, useState} from 'react'
import _ from 'lodash'
import {Box, Typography, Tooltip} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import {getOrderStatus} from '../../helpers/UtilityHelper'
import {useGetCouriersQuery} from '../../redux/commonRTKPersist'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS} from '../../styles/foundation/colors'
import {SHADOW} from '../../styles/foundation/effects'

/**
 * CourierBenchmarkScatter — "Delivery Leaderboard".
 * Client-facing widget: partners ranked by delivery success (highest first), medals for the
 * top three, a single green success bar and the exact %. Returns/RTO are intentionally kept
 * out of the headline so the page never leads with a negative on a merchant/sales screen.
 * Shows the top 8 by default with a Show-all toggle so a long courier list never balloons
 * the card. Computed entirely from the shipment list already loaded — no extra API.
 */
const MIN_VOLUME = 5 // ignore couriers with too few shipments to be meaningful
const MEDALS = ['🥇', '🥈', '🥉']
const COLLAPSED_ROWS = 8 // keep the card compact — show the rest behind a toggle

export default function CourierBenchmarkScatter({data = []}) {
  const {data: allCouriers} = useGetCouriersQuery()
  const [expanded, setExpanded] = useState(false)

  const points = useMemo(() => {
    const infoOf = (id) => {
      const c = allCouriers?.find((cr) => cr.id === id)
      return {name: c?.parent || c?.name || 'Unknown', serviceType: c?.serviceTypePrimary || ''}
    }
    const byCourier = _.groupBy(Array.isArray(data) ? data : [], 'courier')
    return Object.keys(byCourier)
      .map((cid) => {
        const rows = byCourier[cid]
        const vol = rows.length
        let delivered = 0
        rows.forEach((r) => {
          if (getOrderStatus(r.orderStatus) === 'DELIVERED') delivered++
        })
        const {name, serviceType} = infoOf(cid)
        return {id: cid, name, serviceType, volume: vol, deliveryPct: vol ? (delivered / vol) * 100 : 0}
      })
      .filter((p) => p.volume >= MIN_VOLUME)
      // Leaderboard order: highest volume first, delivery % as the tie-breaker.
      .sort((a, b) => b.volume - a.volume || b.deliveryPct - a.deliveryPct)
  }, [data, allCouriers])

  // Only render the top N until the user expands — a 25-courier list would otherwise
  // make the card taller than the whole viewport.
  const canCollapse = points.length > COLLAPSED_ROWS
  const visiblePoints = expanded ? points : points.slice(0, COLLAPSED_ROWS)

  // Fleet average delivery success — drawn as a reference marker on every bar so a merchant
  // instantly sees which partners beat the pack and which lag, without recolouring the card red.
  const avgDeliveryPct = points.length ? _.meanBy(points, 'deliveryPct') : 0
  const LAG = '#c98a1a' // muted amber for below-average % (a soft nudge, not an alarm)

  const TRACK = '#f1f4f8'
  const GRID_COLS = '34px 150px 92px 1fr 48px'
  const sepSx = {borderLeft: `1px solid ${BORDER}`, pl: 1.5}
  const headSx = {fontFamily: FONT, fontSize: '9.5px', fontWeight: WEIGHT.extra, letterSpacing: '0.05em', textTransform: 'uppercase', color: SUBTLE}

  return (
    <Box sx={{bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '10px', boxShadow: SHADOW.card, p: 2.5, mb: 2.5}}>
      <Typography sx={{fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.extra, letterSpacing: '0.07em', textTransform: 'uppercase', color: SUBTLE}}>
        Courier Performance
      </Typography>
      <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mb: 1.5, mt: 0.3}}>
        Your courier partners ranked by shipment volume and delivery success.
      </Typography>

      {points.length === 0 ? (
        <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, py: 3, textAlign: 'center'}}>
          Not enough courier volume in this range to benchmark (needs {MIN_VOLUME}+ shipments per courier).
        </Typography>
      ) : (
        <Box sx={{width: '100%', overflowX: 'auto'}}>
          <Box sx={{minWidth: 520}}>
            {/* header */}
            <Box sx={{display: 'grid', gridTemplateColumns: GRID_COLS, gap: 1.5, alignItems: 'center', pb: 1, borderBottom: `1px solid ${BORDER}`}}>
              <Typography sx={{...headSx, textAlign: 'center'}}>#</Typography>
              <Typography sx={headSx}>Courier</Typography>
              <Typography sx={{...headSx, ...sepSx, textAlign: 'center'}}>Volume</Typography>
              <Typography sx={{...headSx, ...sepSx}}>Delivery percentage</Typography>
              <Typography sx={{...headSx}}></Typography>
            </Box>
            {/* rows — already sorted best-delivery-first */}
            {visiblePoints.map((p, i) => (
              <Box key={p.id} sx={{display: 'grid', gridTemplateColumns: GRID_COLS, gap: 1.5, alignItems: 'center', py: 1.2, borderBottom: `1px solid ${BORDER}`}}>
                <Typography sx={{fontFamily: FONT, fontSize: i < 3 ? '16px' : '13px', fontWeight: WEIGHT.extra, color: SUBTLE, textAlign: 'center', lineHeight: 1}}>
                  {i < 3 ? MEDALS[i] : i + 1}
                </Typography>
                <Box sx={{overflow: 'hidden'}}>
                  <Typography sx={{fontFamily: FONT, fontSize: '12px', fontWeight: WEIGHT.bold, color: NAVY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{p.name}</Typography>
                  {p.serviceType && <Typography sx={{fontFamily: FONT, fontSize: '10px', color: SUBTLE, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3}}>{p.serviceType}</Typography>}
                </Box>
                {/* Total volume — a distinct pill so it doesn't blend with the success bar */}
                <Box sx={{...sepSx, textAlign: 'center'}}>
                  <Box component='span' sx={{display: 'inline-block', bgcolor: '#eef1f5', color: NAVY, fontFamily: FONT, fontWeight: WEIGHT.extra, fontSize: '12px', px: 1.1, py: 0.3, borderRadius: '6px', fontVariantNumeric: 'tabular-nums'}}>
                    {p.volume.toLocaleString()}
                  </Box>
                </Box>
                <Box sx={{...sepSx}}>
                  <Tooltip arrow title={`${p.name}${p.serviceType ? ' - ' + p.serviceType : ''} — ${p.deliveryPct.toFixed(1)}% delivered · ${p.volume.toLocaleString()} shipments · fleet avg ${avgDeliveryPct.toFixed(1)}%`}>
                    <Box sx={{position: 'relative', height: 12, display: 'flex', alignItems: 'center', cursor: 'default'}}>
                      <Box sx={{position: 'absolute', left: 0, right: 0, height: 9, bgcolor: TRACK, borderRadius: '5px', overflow: 'hidden'}}>
                        <Box sx={{height: '100%', width: `${Math.min(p.deliveryPct, 100)}%`, bgcolor: SUCCESS, borderRadius: '5px'}} />
                      </Box>
                      {/* fleet-average reference marker */}
                      {avgDeliveryPct > 0 && (
                        <Box sx={{position: 'absolute', left: `${Math.min(avgDeliveryPct, 100)}%`, top: 0, bottom: 0, width: '2px', bgcolor: NAVY, opacity: 0.55, transform: 'translateX(-1px)'}} />
                      )}
                    </Box>
                  </Tooltip>
                </Box>
                <Typography sx={{fontFamily: FONT, fontSize: '13px', fontWeight: WEIGHT.extra, color: p.deliveryPct >= avgDeliveryPct ? SUCCESS : LAG, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>{p.deliveryPct.toFixed(0)}%</Typography>
              </Box>
            ))}

            {canCollapse && (
              <Box
                onClick={() => setExpanded((v) => !v)}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                  mt: 1.25, py: 0.9, cursor: 'pointer', borderRadius: '8px',
                  color: PRIMARY_BLUE, fontFamily: FONT, fontSize: '12px', fontWeight: WEIGHT.bold,
                  '&:hover': {bgcolor: '#f3f7fe'},
                }}
              >
                {expanded ? 'Show top 8' : `Show all ${points.length} couriers`}
                {expanded ? <KeyboardArrowUpIcon sx={{fontSize: 18}} /> : <KeyboardArrowDownIcon sx={{fontSize: 18}} />}
              </Box>
            )}

          </Box>
        </Box>
      )}
    </Box>
  )
}
